import { Div, Label, Loader, useWindowSize, Touchable, Icon } from "@grassrootly/rn-core";
import { ScrollView, Image, RefreshControl, View, ImageBackground } from "react-native";
import { useCompetitionStats } from "./useCompetitionStats";
import { PlayerRating } from "../../components/shared";
import { images } from "../../layout/assets";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { useMemo } from "react";
import { ISeason } from "@grassrootly/api";

interface CompetitionTopPlayersTabProps {
  competitionId: number;
  seasonId: number;
  season: ISeason | null;
  navigation: MainStackScreenProps<"competition-detail">["navigation"];
  teamSize?: number;
}

export default function CompetitionTopPlayersTab({ competitionId, seasonId, season, navigation, teamSize = 6 }: CompetitionTopPlayersTabProps) {
  const { stats, loading, refreshStats, mode, setMode, month, navigateMonth, isCurrentOrFutureMonth } = useCompetitionStats(competitionId, seasonId);
  const { width } = useWindowSize();

  // Check if we should show awards (past month or inactive season)
  const shouldShowAwards = useMemo(() => {
    if (mode === "monthly") {
      // Show awards for past months only
      return !isCurrentOrFutureMonth;
    } else if (mode === "allseason") {
      // Show awards for inactive seasons only
      return season ? !season.is_active : false;
    }
    return false;
  }, [mode, isCurrentOrFutureMonth, season]);

  // Calculate top team with GK priority - always sorted by average rating
  const topTeam = useMemo(() => {
    if (!stats || stats.length === 0) return [];
    
    // Always sort by average rating for top team (independent of filter selection)
    const sorted = stats.slice().sort((a, b) => {
      return (b.avg_rating ?? 0) - (a.avg_rating ?? 0);
    });
    
    // Separate goalkeepers and field players
    const goalkeepers = sorted.filter(p => p.player_position === 'GK');
    const fieldPlayers = sorted.filter(p => p.player_position !== 'GK');
    
    let selectedPlayers = [];
    
    // If at least one goalkeeper exists, prioritize one GK
    if (goalkeepers.length > 0) {
      const topGK = goalkeepers[0]; // Top-rated goalkeeper
      const topFieldPlayers = fieldPlayers.slice(0, teamSize - 1);
      selectedPlayers = [topGK, ...topFieldPlayers];
    } else {
      // No goalkeeper available, select top teamSize players
      selectedPlayers = sorted.slice(0, teamSize);
    }
    
    // Transform to match expected interface with name and profile_picture
    const result = selectedPlayers.map(player => ({
      ...player,
      name: player.player_name,
      profile_picture: player.player_profile_picture,
    }));
    
    return result;
  }, [stats, teamSize]);

  // Calculate awards for past periods
  const awards = useMemo(() => {
    if (!shouldShowAwards || !stats || stats.length === 0) return null as any;

    // Build arrays of tied winners per category
    const toMaxAndWinnersOn = (source: any[], selector: (x: any) => number, filter?: (x: any) => boolean) => {
      const filtered = (filter ? source.filter(filter) : source).filter((x: any) => selector(x) !== undefined && !isNaN(Number(selector(x))));
      if (!filtered.length) return { max: -Infinity, winners: [] as any[] };
      const max = filtered.reduce((m: number, it: any) => Math.max(m, Number(selector(it) ?? -Infinity)), -Infinity);
      const winners = filtered.filter((it: any) => Number(selector(it)) === max);
      return { max, winners };
    };

    // 50% of MAX_GAMES eligibility for rating-based award
    const maxGames = stats.reduce((m: number, it: any) => Math.max(m, Number(it?.games_played ?? 0)), 0);
    const minRequiredGames = Math.ceil(0.5 * maxGames);
    const eligibleForRating = stats.filter((it: any) => Number(it?.games_played ?? 0) >= minRequiredGames);

    const rating = toMaxAndWinnersOn(eligibleForRating, (x) => x.avg_rating, (x) => typeof x.avg_rating === "number" && x.avg_rating > 0);
    const goals = toMaxAndWinnersOn(stats, (x) => x.goals ?? 0);
    const assists = toMaxAndWinnersOn(stats, (x) => x.assists ?? 0);
    const exp = toMaxAndWinnersOn(stats, (x) => x.points_earned ?? 0);

    // Apply tie-breakers to select a single winner for certain categories
    const pickByFewestGames = (list: any[]) =>
      list
        .slice()
        .sort((a, b) => {
          const ga = typeof a.games_played === "number" ? a.games_played : Number.POSITIVE_INFINITY;
          const gb = typeof b.games_played === "number" ? b.games_played : Number.POSITIVE_INFINITY;
          if (ga !== gb) return ga - gb; // fewer games first
          return 0;
        })[0];

    const pickByMostGames = (list: any[]) =>
      list
        .slice()
        .sort((a, b) => {
          const ga = typeof a.games_played === "number" ? a.games_played : 0;
          const gb = typeof b.games_played === "number" ? b.games_played : 0;
          if (ga !== gb) return gb - ga; // more games first
          return 0;
        })[0];

    const ratingWinners = rating.winners.length > 1 ? [pickByMostGames(rating.winners)].filter(Boolean) : rating.winners;
    const goalsWinners = goals.winners.length > 1 ? [pickByFewestGames(goals.winners)].filter(Boolean) : goals.winners;
    const assistsWinners = assists.winners.length > 1 ? [pickByFewestGames(assists.winners)].filter(Boolean) : assists.winners;

    return {
      rating: ratingWinners,
      goals: goalsWinners,
      assists: assistsWinners,
      exp: exp.winners,
      counts: {
        rating: rating.winners.length,
        goals: goals.winners.length,
        assists: assists.winners.length,
        exp: exp.winners.length,
      },
      originals: {
        rating: rating.winners,
        goals: goals.winners,
        assists: assists.winners,
        exp: exp.winners,
      },
    };
  }, [stats, shouldShowAwards]);

  return (
    <Div classNames={["flex1"]}>
      {loading ? (
        <Div classNames={["flex1", "alignItemsCenter", "justifyContentCenter", "mt-{30}"]}>
          <Loader />
        </Div>
      ) : null}

      <ScrollView refreshControl={<RefreshControl refreshing={loading} onRefresh={refreshStats} />} showsVerticalScrollIndicator={false}>
        {/* Mode toggle - pill chips with icons */}
        <Div classNames={["flexRow", "justifyContentCenter", "mt-{12}", "mb-{12}"]}>
          <Div classNames={["flexRow", "gap8", "bgSecondaryMain", "rounded", "ph8", "pv4", "alignItemsCenter"]}>
            <Touchable onPress={() => setMode("monthly")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "monthly" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="calendar_today"
                    size={16}
                    classNames={[mode === "monthly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "monthly" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "monthly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    Monthly
                  </Label>
                </Div>
              </Div>
            </Touchable>

            <Touchable onPress={() => setMode("allseason")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "allseason" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="calendar_month"
                    size={16}
                    classNames={[mode === "allseason" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "allseason" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "allseason" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    All Season
                  </Label>
                </Div>
              </Div>
            </Touchable>
          </Div>
        </Div>

        {/* Month navigator for monthly mode - pill-style stepper */}
        {mode === "monthly" && (
          <Div classNames={["flexRow", "justifyContentCenter", "mt-{4}", "mb-{20}"]}>
            <Div classNames={["flexRow", "alignItemsCenter", "gap12", "bgSecondaryMain", "rounded", "ph12", "pv4"]}>
              {/* Previous month */}
              <Touchable onPress={() => navigateMonth(-1)}>
                <Icon name="arrow_back_ios" size={20} />
              </Touchable>

              {/* Current month */}
              <Div classNames={["flexRow", "alignItemsCenter", "ph-{12}"]}>
                <Label classNames={["textCenter"]} type="l2Bold">
                  {month}
                </Label>
              </Div>

              {/* Next month (hidden for current/future) */}
              {!isCurrentOrFutureMonth && (
                <Touchable onPress={() => navigateMonth(1)}>
                  <Icon name="arrow_forward_ios" size={20} />
                </Touchable>
              )}
            </Div>
          </Div>
        )}

        {/* Top Team Section with Football Field Background */}
        <Div classNames={["mt-{8}", "mb-{12}", "alignItemsCenter"]}>
          <Label type="l1Bold" classNames={["textCenter"]}>
            {mode === "monthly" 
              ? (isCurrentOrFutureMonth ? "Team of the month race" : `Team of the month - ${month}`)
              : "Team of the season"}
          </Label>
        </Div>

        {/* Empty state when there are no stats/top team */}
        {!loading && (!stats || stats.length === 0 || !topTeam || topTeam.length === 0) && (
          <Div classNames={["mt-{8}", "mb-{20}", "alignItemsCenter"]}>
            <Label type="l2Regular" classNames={["textCenter", "colorSecondaryLight"]}>
              There was no matches played.
            </Label>
          </Div>
        )}

        {!loading && topTeam && topTeam.length > 0 && (() => {
          // Dynamic layout based on team size - arrange in rows
          const backgroundHeight = teamSize === 11 ? 500 : 290;
          
          // Split players into rows based on team size
          let rowSplits: number[] = [];
          if (teamSize === 5) {
            rowSplits = [1, 2, 2]; // GK, 2 players, 2 players
          } else if (teamSize === 6) {
            rowSplits = [1, 3, 2]; // GK, 3 players, 2 players
          } else if (teamSize === 7) {
            rowSplits = [1, 3, 3]; // GK, 3 players, 3 players
          } else if (teamSize === 11) {
            rowSplits = [1, 3, 2, 3, 2]; // GK, 3, 2, 3, 2
          } else {
            // Default fallback
            rowSplits = [1, Math.ceil((teamSize - 1) / 2), Math.floor((teamSize - 1) / 2)];
          }
          
          // Find max players in any row to determine consistent sizing
          const maxPlayersInRow = Math.max(...rowSplits);
          
          const renderPlayer = (player: any, index: number) => {
            // Use consistent size based on max players in any row
            const avatarSize = maxPlayersInRow >= 3 ? 40 : 50;
            const nameType = maxPlayersInRow >= 3 ? "l3Bold" : "l2Bold";
            const groupLogoSize = maxPlayersInRow >= 3 ? 12 : 16;
            
            return (
              <Touchable
                key={`${player.player_id}-${index}`}
                onPress={player && player.player_id ? () => navigation.navigate("eqipa-card", { playerId: player.player_id }) : undefined}
                disabled={!(player && player.player_id)}
              >
                <View style={{ maxWidth: maxPlayersInRow >= 3 ? 100 : 120, alignItems: 'center', gap: 4 }}>
                  <Div classNames={["gap8", "alignItemsCenter", "flexRow"]}>
                    <View style={{ position: 'relative' }}>
                      <Image
                        style={{ height: avatarSize, width: avatarSize, borderRadius: 40 }}
                        resizeMode="cover"
                        source={player.profile_picture ? { uri: player.profile_picture } : images.playerAvatar}
                      />
                      {player.player_position === 'GK' && (
                        <View style={{ 
                          position: 'absolute', 
                          bottom: -4, 
                          right: -4, 
                          backgroundColor: '#3B82F6', 
                          borderRadius: 8, 
                          paddingHorizontal: 4, 
                          paddingVertical: 2 
                        }}>
                          <Label type="l4Bold" classNames={["colorNeutralLighterLight"]}>GK</Label>
                        </View>
                      )}
                    </View>
                    {typeof player.avg_rating === "number" ? (
                      <PlayerRating rating={player.avg_rating} classNames={["p8"]} />
                    ) : null}
                  </Div>
                  <Label type={nameType as any} numberOfLines={1} classNames={["textCenter"]}>{player.name}</Label>
                  {player.group_logo && (
                    <Div classNames={["flexRow", "alignItemsCenter", "gap4", "justifyContentCenter"]}>
                      <Image
                        source={{ uri: player.group_logo }}
                        style={{ width: groupLogoSize, height: groupLogoSize, borderRadius: 16 }}
                      />
                      <Label type="l4Regular" classNames={["colorSecondaryLight"]} numberOfLines={1}>
                        {player.group_name}
                      </Label>
                    </Div>
                  )}
                </View>
              </Touchable>
            );
          };
          
          let startIdx = 0;
          const rowElements = rowSplits.map((count: number, rowIdx: number) => {
            const players = topTeam.slice(startIdx, startIdx + count);
            startIdx += count;
            return (
              <Div key={rowIdx} classNames={["flexRow", "justifyContentCenter", "gap16", "mb-{16}"]}>
                {players.map((player, idx) => renderPlayer(player, idx))}
              </Div>
            );
          });
          
          return (
            <ImageBackground
              source={images.footballFieldBg}
              resizeMode="cover"
              style={{ flex: 1, minHeight: backgroundHeight }}
            >
              {/* Dark overlay to improve text/readability over the pitch background */}
              <View
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.45)",
                }}
              />
              <Div classNames={["alignItemsCenter", "flexColumn", "flex1", "pv-{20}"]}>
                {rowElements}
              </Div>
            </ImageBackground>
          );
        })()}

        {/* Awards Section */}
        {shouldShowAwards && awards && (
          <Div classNames={["mt-{20}", "mb-{16}"]}>
            <Div classNames={["mb-{12}", "alignItemsCenter"]}>
              <Label type="l1Bold" classNames={["textCenter"]}>{mode === "monthly" ? `Awards - ${month}` : "Awards"}</Label>
            </Div>
            {(() => {
              const { rating, goals, assists, exp, counts, originals } = awards;
              const items = [
                { key: "rating", title: mode === "monthly" ? "Player of the Month" : "Player of the Season", items: rating, originalItems: originals.rating, kind: "rating" as const, extraCount: Math.max(0, (counts?.rating ?? 0) - 1) },
                { key: "goals", title: "Top Scorer Award", items: goals, originalItems: originals.goals, kind: "goals" as const, extraCount: Math.max(0, (counts?.goals ?? 0) - 1) },
                { key: "assists", title: "Most Assists Award", items: assists, originalItems: originals.assists, kind: "assists" as const, extraCount: Math.max(0, (counts?.assists ?? 0) - 1) },
                { key: "exp", title: "Most EXP gained Award", items: exp, originalItems: originals.exp, kind: "exp" as const, extraCount: Math.max(0, (counts?.exp ?? 0) - 1) },
              ];

              const renderRight = (it: any, kind: "rating" | "goals" | "assists" | "exp") => {
                if (!it) return null;
                if (kind === "rating" && typeof it.avg_rating === "number" && it.avg_rating > 0) {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                      <PlayerRating rating={Number(Number(it.avg_rating).toFixed(2))} decimalPlaces={2} classNames={["p-{4}"]} />
                    </Div>
                  );
                }
                if (kind === "goals") {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                      <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                        {it.goals ?? 0}
                      </Label>
                      <Icon name="sports_soccer" size={18} />
                    </Div>
                  );
                }
                if (kind === "assists") {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                      <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                        {it.assists ?? 0}
                      </Label>
                      <Image source={images.assist} style={{ height: 14, width: 24 }} />
                    </Div>
                  );
                }
                if (kind === "exp") {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                      <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Bold" : "l3Bold"}>
                        {it.points_earned ?? 0}
                      </Label>
                    </Div>
                  );
                }
              };

              // group into rows of 2
              const rows: typeof items[] = [] as any;
              for (let i = 0; i < items.length; i += 2) {
                rows.push(items.slice(i, i + 2));
              }

              const avatarSize = width > 720 ? 88 : 68;

              return rows.map((rowItems, rowIdx) => (
                <Div key={`awards-row-${rowIdx}`} classNames={["flexRow", "gap16", "mb-{12}"]}>
                  {rowItems.map(({ key, title, items: winners, originalItems, kind, extraCount }, colIdx) => (
                    <Touchable
                      key={`${key}-${colIdx}`}
                      classNames={["flex1", `${width > 720 ? "p16" : "p12"}`, "bgSecondaryMain", "rounded", "flexColumn", "gap8"]}
                      onPress={undefined}
                      disabled={!winners || winners.length === 0}
                    >
                      {/* Title on top of card */}
                      <Div classNames={["pl-{12}", "pt-{8}"]}>
                        <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
                          {kind === "rating" && <Icon name="emoji_events" size={width > 720 ? 22 : 18} />}
                          {kind === "goals" && <Icon name="sports_soccer" size={width > 720 ? 22 : 18} />}
                          {kind === "assists" && (
                            <Image source={images.assist} style={{ height: width > 720 ? 16 : 14, width: width > 720 ? 28 : 24 }} />
                          )}
                          {kind === "exp" && <Icon name="analytics" size={width > 720 ? 22 : 18} />}
                          <Label type={width > 720 ? "l2Bold" : "l3Bold"}>{title}</Label>
                        </Div>
                      </Div>

                      {Array.isArray(winners) && winners.length > 0 && (
                        <Div classNames={["flexColumn", "gap8", "flex1", "pl-{8}", "pb-{12}"]}>
                          {winners.map((it: any, idx: number) => (
                            <Touchable
                              key={`${key}-winner-${it.player_id ?? idx}`}
                              onPress={it && it.player_id ? () => navigation.navigate("eqipa-card", { playerId: it.player_id }) : undefined}
                              disabled={!(it && it.player_id)}
                            >
                            <Div classNames={["flexColumn", "gap8"]}>
                              {/* Top row: Avatar and metric */}
                              <Div classNames={["flexRow", "alignItemsCenter", "gap16"]}>
                                <Div classNames={["alignItemsCenter"]}>
                                  {Array.isArray(originalItems) && originalItems.length > 1 ? (() => {
                                    const base = Math.max(48, Math.min(72, avatarSize - 4));
                                    const second = originalItems.find((p: any) => p?.player_id !== it?.player_id);
                                    const hasSecond = !!second;
                                    return (
                                      <View style={{ width: hasSecond ? base + Math.floor(base * 0.4) : base, height: base, position: "relative" }}>
                                        <Image
                                          style={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            height: base,
                                            width: base,
                                            borderRadius: base,
                                            zIndex: 2,
                                          }}
                                          resizeMode="cover"
                                          source={it?.player_profile_picture ? { uri: it.player_profile_picture } : images.playerAvatar}
                                        />
                                        {hasSecond && (
                                          <Image
                                            style={{
                                              position: "absolute",
                                              left: Math.floor(base * 0.4),
                                              top: 0,
                                              height: base,
                                              width: base,
                                              borderRadius: base,
                                              zIndex: 1,
                                            }}
                                            resizeMode="cover"
                                            source={second?.player_profile_picture ? { uri: second.player_profile_picture } : images.playerAvatar}
                                          />
                                        )}
                                      </View>
                                    );
                                  })() : (
                                    <Div classNames={["bgPrimaryWashed", "p8", "rounded", "alignItemsCenter"]}>
                                      <Image
                                        style={{ height: Math.max(48, Math.min(72, avatarSize - 4)), width: Math.max(48, Math.min(72, avatarSize - 4)), borderRadius: Math.max(48, Math.min(72, avatarSize - 4)) }}
                                        resizeMode="cover"
                                        source={it?.player_profile_picture ? { uri: it.player_profile_picture } : images.playerAvatar}
                                      />
                                    </Div>
                                  )}
                                </Div>
                                <Div classNames={["flexRow", "alignItemsCenter", "gap8", "pr-{12}", "ml-{auto}"]}>{renderRight(it, kind)}</Div>
                              </Div>
                              
                              {/* Bottom section: Names centered full width */}
                              <Div classNames={["w100", "alignItemsCenter", "justifyContentCenter"]}>
                                <Label numberOfLines={2} ellipsizeMode="tail" type={width > 720 ? "l2Bold" : "l3Bold"} classNames={["textCenter"]}>
                                  {it?.player_name ?? "—"}
                                </Label>
                                {Array.isArray(originalItems) && originalItems.length > 1 && (
                                  <Div classNames={["flexColumn", "alignItemsCenter", "gap4", "justifyContentCenter", "w100", "mt-{4}"]}>
                                    {originalItems
                                      .filter((p: any) => p?.player_id !== it?.player_id)
                                      .map((p: any, idx2: number) => (
                                        <Label key={`extra-${p?.player_id ?? idx2}`} type={width > 720 ? "l2Bold" : "l3Bold"} classNames={["textCenter"]}>
                                          {p?.player_name}
                                        </Label>
                                      ))}
                                  </Div>
                                )}
                              </Div>
                            </Div>
                            </Touchable>
                          ))}
                        </Div>
                      )}
                    </Touchable>
                  ))}
                </Div>
              ));
            })()}
          </Div>
        )}
      </ScrollView>
    </Div>
  );
}
